/**
 * fieldMappings.ts
 * 
 * Mapeo entre nombres de campos en español (para mostrar en el PDF) 
 * y las propiedades del objeto dataObjects
 */

import dayjs from "dayjs";

export type FieldFormatter = (dataObjects: any) => string;

/**
 * Mapeo de nombres de campos a funciones que extraen el valor de dataObjects
 */
export const fieldMappings: Record<string, FieldFormatter> = {
  // Cabecera
  'Instalación': (data) => {
    const inst = data?.installation;
    if (!inst) return '';
    if (inst.label) return inst.label;
    return inst?.codigo || '';
  },
  'Tipo de muestra': (data) => {
    const sampleType = data?.sample_type;
    if (!sampleType) return '';
    if (sampleType.label) return sampleType.label;
    return sampleType?.codigo || '';
  },
  'Análisis compartido': (data) => data?.shared ? 'Sí' : 'No',
  'Análisis Compartido': (data) => data?.shared ? 'Sí' : 'No',
  'Ref. Cliente': (data) => data?.client_reference || '',
  'Acumulado': (data) => {
    const acc = data?.accumulation;
    if (!acc) return '';
    if (acc.label) return acc.label;
    return acc?.code || '';
  },
  'Motivo': (data) => {
    const reason = data?.reason;
    if (!reason) return '';
    if (reason.label) return reason.label;
    return reason?.codigo || '';
  },
  'Punto de muestreo': (data) => {
    const samplingPoint = data?.sampling_points;
    if (!samplingPoint) return '';
    if (samplingPoint.label) return samplingPoint.label;
    return samplingPoint?.codigo || '';
  },
  'Nº muestras': (data) => data?.sample_number?.toString() || '',

  // Preparación y contaje
  'Analista': (data) => {
    const analyst = data?.calculation_analyst || data?.analysis_analyst;
    if (!analyst) return '';
    if (analyst.label) return analyst.label;
    return analyst?.username || '';
  },
  'Fecha y hora inicio toma de muestra': (data) => {
    if (!data?.date_start_sampling) return '';
    const value = String(data.date_start_sampling);
    // Si ya está formateada (contiene /), devolver tal cual
    if (value.includes('/')) return value;
    return dayjs(data.date_start_sampling).format('DD/MM/YYYY HH:mm');
  },
  'Fecha y hora inicio de toma de muestra': (data) => {
    if (!data?.date_start_sampling) return '';
    const value = String(data.date_start_sampling);
    if (value.includes('/')) return value;
    return dayjs(data.date_start_sampling).format('DD/MM/YYYY HH:mm');
  },
  'Fecha y hora fin toma de muestra': (data) => {
    if (!data?.date_end_sampling) return '';
    const value = String(data.date_end_sampling);
    if (value.includes('/')) return value;
    return dayjs(data.date_end_sampling).format('DD/MM/YYYY HH:mm');
  },
  'Fecha y hora fin de toma de muestra': (data) => {
    if (!data?.date_end_sampling) return '';
    const value = String(data.date_end_sampling);
    if (value.includes('/')) return value;
    return dayjs(data.date_end_sampling).format('DD/MM/YYYY HH:mm');
  },
  'Cantidad de muestra': (data) => data?.calculation_amount_sample || '',
  'Fecha de contaje': (data) => {
    if (!data?.counting_date) return '';
    const value = String(data.counting_date);
    if (value.includes('/')) return value;
    return dayjs(data.counting_date).format('DD/MM/YYYY');
  },
  'Fecha contaje': (data) => {
    if (!data?.counting_date) return '';
    const value = String(data.counting_date);
    if (value.includes('/')) return value;
    return dayjs(data.counting_date).format('DD/MM/YYYY');
  },
  
  // CPM
  'Cpm brutas de la muestra': (data) => data?.total_cpm_sample || data?.calculation_total_cpm_sample || data?.sample_cpm || '',
  'cpm brutas de la muestra': (data) => data?.total_cpm_sample || data?.calculation_total_cpm_sample || data?.sample_cpm || '',
  'cpm brutas muestra': (data) => data?.total_cpm_sample || data?.calculation_total_cpm_sample || data?.sample_cpm || '',
  'Cpm brutas muestra': (data) => data?.total_cpm_sample || data?.calculation_total_cpm_sample || data?.sample_cpm || '',
  'cpm brutas blanco': (data) => data?.gross_white_cpm || data?.counting?.gross_white_cpm || data?.calculation?.gross_target_cpm || '',
  'Cpm brutas de la muestra ventana alfa': (data) => data?.alpha_window_cpm || data?.sample_cpm_alpha || data?.first_counting_cpm || '',
  'cpm brutas de la muestra ventana alfa': (data) => data?.alpha_window_cpm || data?.sample_cpm_alpha || data?.first_counting_cpm || '',
  'Cpm brutas de la muestra ventana beta': (data) => data?.beta_window_cpm || data?.sample_cpm_beta || data?.second_counting_cpm || '',
  'cpm brutas de la muestra ventana beta': (data) => data?.beta_window_cpm || data?.sample_cpm_beta || data?.second_counting_cpm || '',
  'cpm brutas de la muestra en ventana alfa': (data) => data?.alpha_window_cpm || data?.sample_cpm_alpha || data?.first_counting_cpm || '',
  'cpm brutas de la muestra en ventana beta': (data) => data?.beta_window_cpm || data?.sample_cpm_beta || data?.second_counting_cpm || '',
  'Cpm brutas muestra 1º Contaje': (data) => data?.first_counting_cpm || data?.sample_cpm || '',
  'Cpm brutas muestra 2º contaje': (data) => data?.second_counting_cpm || '',

  // Código fondo
  'Código fondo': (data) => {
    const fondo = data?.calculation_fondo || data?.background_code || data?.white_code;
    if (!fondo) return '';
    
    // Manejar formato {label, value}
    if (fondo.label) return fondo.label;
    
    // Manejar formato con proceso y tipo_codigo
    if (fondo.proceso && fondo.tipo_codigo) {
      return fondo.proceso + '-' + fondo.tipo_codigo;
    }
    
    // Manejar formato con code
    return fondo?.code || '';
  },
  'Código de fondo alfa': (data) => {
    const fondo = data?.calculation_fondo_alfa || data?.fondo_alfa || data?.alpha_background_code || data?.background_code_alpha || data?.calculation_fondo;
    if (!fondo) return '';
    if (fondo.label) return fondo.label;
    // Manejar formato con proceso y tipo_codigo
    if (fondo.proceso && fondo.tipo_codigo) {
      let result = fondo.proceso + '-' + fondo.tipo_codigo;
      if (fondo.medio_filtro) {
        result += '-' + fondo.medio_filtro;
      }
      return result;
    }
    return fondo?.code || fondo?.codigo || String(fondo);
  },
  'Código de fondo beta': (data) => {
    const fondo = data?.calculation_fondo_beta || data?.fondo_beta || data?.beta_background_code || data?.background_code_beta || data?.calculation_fondo;
    if (!fondo) return '';
    if (fondo.label) return fondo.label;
    // Manejar formato con proceso y tipo_codigo
    if (fondo.proceso && fondo.tipo_codigo) {
      let result = fondo.proceso + '-' + fondo.tipo_codigo;
      if (fondo.medio_filtro) {
        result += '-' + fondo.medio_filtro;
      }
      return result;
    }
    return fondo?.code || fondo?.codigo || String(fondo);
  },

  // Tiempos
  'Tiempo contaje muestra': (data) => data?.total_time_counting || data?.first_counting_time || '',
  'Tiempo contaje blanco': (data) => data?.total_white_counting_time || '',
  'Tiempo contaje': (data) => data?.total_time_counting || data?.calculation_time_count || '',
  'Tiempo de muestreo': (data) => data?.sampling_time || data?.calculation_sampling_time_min || '',

  // Pesos y rendimientos
  'Peso precipitado': (data) => {
    return data?.calculation_mg_torta || 
           data?.mg_torta || 
           data?.precipitate_weight || 
           data?.calculation_tsie_white ||
           '';
  },
  'Peso precipitado (mg)': (data) => {
    return data?.calculation_planchette_with_precipitate || 
           data?.calculation_mg_torta || 
           data?.mg_torta || 
           data?.precipitate_weight || 
           data ?.calculation_tsie_white ||
           '';
  },
  'Rendimiento': (data) => {
    return data?.calculation_performance || 
           data?.preparation_performance || 
           data?.yield || 
           data?.chemical_yield || 
           data?.counting_by_one ||
           '';
  },
  'Rendimiento cálculo': (data) => {
    return data?.calculation_performance || 
           data?.calculation_yield || 
           data?.yield || 
           '';
  },
  'mg de potasio (K)': (data) => {
    const value = data?.calculation_mg_potasio || data?.mg_potasio || data?.potassium_mg;
    if (!value) return '';
    return String(value);
  },

  // TSIE
  'TSIE blanco': (data) => data?.calculation_tsie_white || data?.tsie_white || data?.tsie_blanco || '',
  'TSIE muestra': (data) => data?.calculation_tsie_sample || data?.sqp_tsie_sample || data?.tsie_muestra || '',
  'TSIE muestras': (data) => data?.sqp_tsie_sample || '',

  // Compartida
  'Compartida': (data) => data?.calculation_shared || '',

  // Información del laboratorio
  'Laboratorio': (data) => {
    const lab = data?.laboratory;
    if (!lab) return '';
    // Manejar formato {label, value}
    if (lab.label) return lab.label;
    return lab?.descripcion || '';
  },
  'Unidades': (data) => {
    const units = data?.calculation_units || data?.units;
    if (!units) return '';
    // Manejar formato {label, value}
    if (units.label) return units.label;
    return units?.descripcion || '';
  },
  'Equipo de contaje': (data) => {
    const equipment = data?.calculation_equipment || data?.counting_equipment;
    
    if (Array.isArray(equipment) && equipment.length > 0) {
      const item = equipment.find(eq => eq?.is_geometry === false) || equipment[0];
      if (item?.label) return item.label;
      return item?.codigo || item?.descripcion || '';
    }
    if (equipment?.label) return equipment.label;
    return equipment?.codigo || equipment?.descripcion || '';
  },
  'Equipo contaje': (data) => {
    const equipment = data?.calculation_equipment || data?.counting_equipment;
    
    if (Array.isArray(equipment) && equipment.length > 0) {
      const item = equipment.find(eq => eq?.is_geometry === false) || equipment[0];
      if (item?.label) return item.label;
      return item?.codigo || item?.descripcion || '';
    }
    if (equipment?.label) return equipment.label;
    return equipment?.codigo || equipment?.descripcion || '';
  },
  'Equipo de medida': (data) => {
    const equipment = data?.measurement_equipment || data?.calculation_equipment || data?.counting_equipment;
    
    if (Array.isArray(equipment) && equipment.length > 0) {
      const item = equipment.find(eq => eq?.is_geometry === false) || equipment[0];
      if (item?.label) return item.label;
      return item?.codigo || item?.descripcion || '';
    }
    if (equipment?.label) return equipment.label;
    return equipment?.codigo || equipment?.descripcion || '';
  },
  'Detector': (data) => {
    const detector = data?.calculation_detector || data?.counting_detector || data?.detector;
    
    if (Array.isArray(detector) && detector.length > 0) {
      const item = detector[0];
      if (item?.label) return item.label;
      return item?.name || item?.codigo || item?.descripcion || '';
    }
    if (detector?.label) return detector.label;
    return detector?.name || detector?.codigo || detector?.descripcion || data?.detector_code || detector ||'';
  },
  'Procedimiento': (data) => {
    const proc = data?.preparation_procedure || data?.procedure || data?.calculation_procedure_mlpma;
    if (!proc) return '';
    if (proc.label) return proc.label;
    return proc?.numop || '';
  },
  'Procedimiento MLPMA': (data) => {
    const proc = data?.preparation_procedure;
    if (!proc) return '';
    if (proc.label) return proc.label;
    return proc?.numop || '';
  },

  // Relaciones
  'Relación Peso Húmedo/Seco': (data) => {
    debugger;
    const value = data?.wet_dry || data?.calculation_tsie_sample || ""
    if (!value) return '';
    return String(value);
  },

  // Volumen
  'Volumen aire analizado': (data) => data?.analyzedAirVolume || data?.air_volume || '',
  'Volumen trazador': (data) => data?.calculation_volume || data?.tracer_volume || '',

  // Tipo de preparación
  'Tipo de Preparación': (data) => {
    const prepType = data?.typePreparation || data?.preparation_type;
    if (!prepType) return '';
    if (prepType.label) return prepType.label;
    return prepType?.name || '';
  },
  'Tipo de Preparación: Evaporación': (data) => 'Evaporación',

  // Específicos de Sr89/Sr90
  'Variación t1': (data) => data?.calculation_precipitation_first_counting || '',
  'Variación t2': (data) => data?.calculation_precipitation_second_counting || '',
  'Fecha y h. final muestreo-Fec y h. precipitado': (data) => data?.sampling_precipitate_time_diff || data?.calculation_precipitation_final_time || '',

  // Específicos de Dosimetría
  'Días de exposición': (data) => data?.exposition_days?.toString() || data?.exposure_days?.toString() || '',
  'Días de tránsito': (data) => data?.transit_days?.toString() || '',
  'Fecha de borrado': (data) => {
    const dateValue = data?.calculation_delete_date || data?.erase_date;
    if (!dateValue) return '';
    const value = String(dateValue);
    if (value.includes('/')) return value;
    return dayjs(dateValue).format('DD/MM/YYYY');
  },
  'Fecha de lectura': (data) => {
    const dateValue = data?.calculation_read_date || data?.reading_date;
    if (!dateValue) return '';
    const value = String(dateValue);
    if (value.includes('/')) return value;
    return dayjs(dateValue).format('DD/MM/YYYY');
  },
  'Lectura media (µSv)': (data) => data?.valor_media_lecturas_usv?.toString() || data?.average_reading?.toString() || '',
  'Error lecturas (µSv)': (data) => data?.error_lecturas_usv?.toString() || data?.reading_error?.toString() || '',
  'Nº de dosímetro': (data) => {
    const dosimetro = data?.dosimetro;
    if (!dosimetro) return '';
    if (dosimetro.label) return dosimetro.label;
    return dosimetro?.numero?.toString() || dosimetro?.number?.toString() || '';
  },
  'Grupo': (data) => {
    const group = data?.group;
    if (!group) return '';
    if (group.label) return group.label;
    if (group.value) return group.value;
    return String(group);
  },

  // Específicos de Espectrometría Alfa
  'Fondo': (data) => {
    const fondo = data?.calculation_background || data?.background || data?.background_code;
    if (!fondo) return '';
    if (fondo.label) return fondo.label;
    if (typeof fondo === 'string') {
      if (fondo.includes('\\')) {
        const parts = fondo.split('\\');
        return parts[parts.length - 1];
      }
      return fondo;
    }
    return fondo?.codigo || fondo?.code || '';
  },
  'Calibración': (data) => {
    const cal = data?.calculation_calibration || data?.calibration;
    if (!cal) return '';
    // Handle {label, value} format
    if (cal.label) return cal.label;
    // Handle string date
    if (typeof cal === 'string') {
      // Si ya está formateada, devolver tal cual
      if (cal.includes('/')) return cal;
      return cal;
    }
    // Handle object with date
    if (cal?.date) {
      const dateStr = String(cal.date);
      if (dateStr.includes('/')) return dateStr;
      return dayjs(cal.date).format('DD/MM/YYYY');
    }
    // Fallback to string conversion
    return String(cal);
  },
  'Certificado trazador': (data) => {
    const cert = data?.calibration_source_certificate || data?.tracer_certificate;
    if (!cert) return '';
    if (cert.label) return cert.label;
    return String(cert);
  },
  'Librería': (data) => {
    const lib = data?.calculation_library || data?.library;
    if (!lib) return '';
    if (lib.label) return lib.label;
    if (typeof lib === 'string') {
      if (lib.includes('\\')) {
        const parts = lib.split('\\');
        return parts[parts.length - 1];
      }
      return lib;
    }
    return lib?.codigo || lib?.name || String(lib);
  },

  // Específicos de Gamma
  'Geometría': (data) => {
    const equipment = data?.measurement_equipment || data?.calculation_equipment || data?.counting_equipment;
    
    if (Array.isArray(equipment) && equipment.length > 0) {
      const item = equipment.find(eq => eq?.is_geometry === true) || equipment[0];
      if (item?.label) return item.label;
      return item?.codigo || item?.descripcion || '';
    }
    if (equipment?.label) return equipment.label;
    return equipment?.codigo || equipment?.descripcion || '';
  },
  'Altura': (data) => data?.height || data?.calculation_height || '',
  'Factor Geométrico': (data) => data?.geometric_factor || data?.calculation_geometric_factor || '',
  'Decaimiento (min)': (data) => data?.decay_minutes || data?.calculation_decay_minutes || data?.decay_min || '',
  'Factor de Autoabsorción': (data) => data?.self_absorption_factor || data?.calculation_self_absorption_factor || '',
  'Cuentas pico 351 Kev': (data) => data?.counts_351kev || data?.calculation_counts_351kev || '',
  'Cuentas pico 911 Kev': (data) => data?.counts_911kev || data?.calculation_counts_911kev || '',

  // Campos específicos de Tritio - Volumen de aire analizado
  'Volumen de aire analizado (m3)': (data) => {
    // Volumen de aire analizado para H3-V
    const volumen = data?.analyzedAirVolume || data?.analyzed_air_volume || data?.calculation_analyzed_air_volume || data?.air_volume || '';
    return volumen ? String(volumen) : '';
  },

  // Campos específicos de Ni63/Fe55
  'cpm muestra final': (data) => data?.final_sample_cpm || data?.calculation_final_sample_cpm || data?.cpm_muestra_final || data?.total_cpm_sample || data?.calculation_total_cpm_sample || data?.sample_cpm || '',
  'cpm blanco final': (data) => data?.final_white_cpm || data?.calculation_final_white_cpm || data?.cpm_blanco_final || data?.gross_white_cpm || data?.counting?.gross_white_cpm || data?.calculation?.gross_target_cpm || '',
  'cpm blanco brutas': (data) => data?.gross_white_cpm,
  'cpm muestra brutas': (data) => data?.sample_cpm,
  'cpm quimioluminiscencia blanco.': (data) => data?.cpm_white_quimioluminiscencia || '',
  'cpm quimioluminiscencia sample.': (data) => data?.cpm_sample_quimioluminiscencia || '',
  'Cantidad masa vial': (data) => data?.vial_mass_quantity || data?.calculation_vial_mass_quantity || data?.masa_vial || data?.vial_sample_quantity || '',
};

/**
 * Obtiene el valor formateado de un campo para mostrar en el PDF
 * @param fieldName - Nombre del campo en español
 * @param dataObjects - Objeto con los datos
 * @returns Valor formateado como string
 */
export const getFieldValue = (fieldName: string, dataObjects: any): string => {
  const formatter = fieldMappings[fieldName];
  if (!formatter) {
    console.warn(`No se encontró mapeo para el campo: ${fieldName}`);
    return '';
  }
  
  const value = formatter(dataObjects);
  
  // Ensure we always return a string, never an object
  if (value === null || value === undefined) {
    return '';
  }
  
  // If it's an object (like {label, value}), extract the label or value
  if (typeof value === 'object') {
    const objValue = value as any;
    if (objValue.label) return String(objValue.label);
    if (objValue.value) return String(objValue.value);
    console.warn('Campo retornó un objeto:', fieldName, value);
    return String(value);
  }
  
  return String(value);
};
